/**
 * Hook to add a Statsig feature flag.
 *
 * Adding a new feature flag:
 * 1. Add the gate name to FEATURE_FLAGS in `frontend/src/featureFlags.ts`
 * 2. Use this hook in your component:
 *    ```
 *    import { useFeatureGate } from '~hooks/access/useFeatureGate'
 *    import { FEATURE_FLAGS } from '~featureFlags'
 *
 *    const { enabled } = useFeatureGate(FEATURE_FLAGS.MY_NEW_FLAG)
 *    ```
 * 3. Conditionally render based on `enabled`
 * 4. Create the gate in the Statsig dashboard with the same name string
 *
 * The hook automatically reads userId, isSuperUser, organizationHandle, and the
 * selected workspace from Redux and calls updateUserAsync to sync the Statsig user.
 * Callers can provide a device ID and an organization handle explicitly when they
 * are not available in Redux (e.g. anonymous chat/search embeds, where no
 * organization is selected and the handle only exists in the URL). Without a
 * handle no `organization` / `organization_handle` custom field is sent, so gate
 * rules targeting an organization cannot match.
 * Logged-out/device-level targeting uses the `Device ID` custom ID, which
 * requires the gate's unit type to be that ID type in the Statsig dashboard.
 * `enabled` is false until the first user update resolves; subsequent updates
 * (e.g. after an org switch) keep the last-known value while re-evaluating.
 * `workspace_id` / `workspace_name` match the backend `StatsigCustomField` values, so a
 * dashboard rule on them applies to both frontend and backend checks.
 *
 * To pass other custom fields (e.g. an org list):
 *    ```
 *    const { enabled } = useFeatureGate(FEATURE_FLAGS.MY_FLAG, {
 *      custom: { organizationsList: someOrgList },
 *    })
 *    ```
 * Statsig will automatically recognize any new custom attributes you pass.
 * Just make sure to also add them to the criteria in the Statsig dashboard.
 *
 * Note: Statsig maintains a single user context. This hook merges new custom
 * attributes and IDs with the existing ones (via `getContext().user`) to avoid
 * multiple hook instances overwriting each other's values.
 *
 * Cleanup when fully rolled out:
 * 1. Remove the entry from FEATURE_FLAGS in featureFlags.ts
 * 2. Remove the useFeatureGate call and conditional rendering
 * 3. Archive the gate in the Statsig dashboard
 */

import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useStatsigClient, useStatsigUser } from '@statsig/react-bindings'

import { getOrCreateDeviceId } from '~AmplitudeInit'
import { IS_PROD } from '~config'
import { type RootState } from '~redux/rootReducer'

import { DEVICE_ID_KEY } from '../../statsigClient'

/**
 * Dev-only escape hatch for flipping a feature gate locally.
 *
 * Statsig is not configured locally (no client key), so every gate resolves to
 * `false`. To test a gated feature without a deploy, set an override in the
 * browser console and reload:
 *   localStorage.setItem('ff_override:branching_workflows', 'true')
 *   localStorage.removeItem('ff_override:branching_workflows') // clear it
 *
 * Overrides are ignored in production.
 */
const getLocalGateOverride = (gateName: string): boolean | undefined => {
  if (IS_PROD) return undefined
  try {
    const raw = window.localStorage.getItem(`ff_override:${gateName}`)
    if (raw === 'true') return true
    if (raw === 'false') return false
  } catch {
    // localStorage may be unavailable (e.g. SSR, privacy mode)
  }
  return undefined
}

type StatsigCustomAttributes = Record<string, string | number | boolean | Array<string> | undefined>

type FeatureGateOptions = {
  custom?: StatsigCustomAttributes
  defaultValue?: boolean
  deviceId?: string
  organizationHandle?: string
}

export const useFeatureGate = (
  gateName: string,
  options?: FeatureGateOptions,
): { enabled: boolean; loading: boolean } => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const isSuperUser = Boolean(useSelector((state: RootState) => state?.auth.user?.super))
  const selectedOrganizationHandle = useSelector(
    (state: RootState) => state?.organization.selectedOrganization?.handle,
  )
  const organizationHandle = options?.organizationHandle ?? selectedOrganizationHandle
  const workspaceId = useSelector((state: RootState) => state?.organization.selectedWorkspace?.id)
  const workspaceName = useSelector(
    (state: RootState) => state?.organization.selectedWorkspace?.name,
  )

  const { client: statsigClient } = useStatsigClient()
  const { updateUserAsync } = useStatsigUser()
  const customKey = JSON.stringify(options?.custom)
  const userKey = JSON.stringify([
    userId,
    isSuperUser,
    organizationHandle,
    workspaceId,
    workspaceName,
    options?.deviceId,
    customKey,
  ])
  // Every committed user-key change starts a numbered update; the gate keeps its
  // last-known value until the latest one resolves so an org switch never flickers.
  const requestSeqRef = useRef(0)
  const [pendingSeq, setPendingSeq] = useState(0)
  const [resolvedSeq, setResolvedSeq] = useState(0)
  const [committedUserKey, setCommittedUserKey] = useState<string | null>(null)
  const lastEnabledRef = useRef<boolean | null>(null)
  const statsigReady = resolvedSeq > 0
  const updating = resolvedSeq !== pendingSeq || committedUserKey !== userKey

  useEffect((): (() => void) => {
    let isCurrent = true
    requestSeqRef.current += 1
    const seq = requestSeqRef.current
    setPendingSeq(seq)
    setCommittedUserKey(userKey)
    const existingUser = statsigClient.getContext().user
    const existingCustom = existingUser?.custom ?? {}
    const existingCustomIDs = existingUser?.customIDs ?? {}
    const resolvedDeviceId =
      options?.deviceId ??
      (organizationHandle ? getOrCreateDeviceId(organizationHandle) : undefined)
    const customIDs = {
      ...existingCustomIDs,
      ...(resolvedDeviceId ? { [DEVICE_ID_KEY]: resolvedDeviceId } : {}),
    }
    updateUserAsync({
      userID: userId ?? 'anonymous',
      customIDs: Object.keys(customIDs).length > 0 ? customIDs : undefined,
      custom: {
        ...existingCustom,
        isSuperUser: isSuperUser ?? false,
        // TODO: Remove organization once all gates have migrated off of it
        ...(organizationHandle
          ? { organization: organizationHandle, organization_handle: organizationHandle }
          : {}),
        workspace_id: workspaceId ?? undefined,
        workspace_name: workspaceName ?? undefined,
        ...options?.custom,
      },
    })
      .catch((err) => {
        console.error('Error updating user for Statsig:', err)
      })
      .finally(() => {
        if (isCurrent) setResolvedSeq(seq)
      })
    return (): void => {
      isCurrent = false
    }
  }, [userKey])

  const localOverride = getLocalGateOverride(gateName)
  if (localOverride !== undefined) {
    return { enabled: localOverride, loading: false }
  }

  const defaultValue = options?.defaultValue ?? false
  let enabled: boolean
  if (!statsigReady) {
    enabled = defaultValue
  } else if (updating && lastEnabledRef.current !== null) {
    enabled = lastEnabledRef.current
  } else {
    enabled = statsigClient.checkGate(gateName)
    lastEnabledRef.current = enabled
  }

  return { enabled, loading: !statsigReady }
}
